import React, { useMemo, useRef } from 'react';
import { useSettings } from '../SettingsProvider';

import { BookmarkRemoveService } from './BookmarksRemove';
import { useBaseMapSnapshots } from '../BaseMap/useBaseMapSnapShots';
import { getOsmUrlHeadersFromSettings } from '../../utils/getOsmUrlHeadersFromSettings';
import { BaseLayerConfig } from '../BaseMap/useBaseLayerConfigs';
import { fromLonLat } from 'ol/proj';
import { BookmarksAddEdit } from './BookmarksAddEdit';
import { Bookmark, useGetShareGroups } from '@ais-ui/websockets';
import { useProfile } from '@ais-ui/profile';
import { PiUser, PiUsersThree, PiUsersThreeThin } from 'react-icons/pi';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ais-ui/ui-core';
import { grabBookmarkInitialGroups } from '../../Groups/grabInitialGroups';

interface BookmarkItemProps {
  bookmark: Bookmark;
  mapHeight: number;
  aspectRatioString: string;
  snapshots: Record<string, string>;
}

const BookmarkItem = ({
  bookmark,
  mapHeight,
  aspectRatioString,
  snapshots,
}: BookmarkItemProps) => {
  const { username } = useProfile();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const groupNames = useMemo(() => {
    if (groups) {
      const bookmarkGroups = grabBookmarkInitialGroups(bookmark, groups);
      return bookmarkGroups.map((group) => group.label).join(', ');
    }
    return '';
  }, [bookmark, groups]);

  return (
    <div
      key={bookmark.uuid}
      className="relative grid grid-cols-[auto_1fr_auto] gap-4 rounded-lg border border-border bg-card px-4 py-6"
    >
      <BookmarkRemoveService
        bookmark={bookmark}
        className="absolute top-1 right-1"
      ></BookmarkRemoveService>
      <div className="flex flex-col space-y-4">
        <img
          alt={`${bookmark.name} Preview`}
          className="rounded-md"
          height={mapHeight}
          width="120"
          src={snapshots[bookmark.uuid] || '/Paper map-bro.png'}
          style={{
            aspectRatio: aspectRatioString,
            objectFit: 'cover',
          }}
        />
      </div>
      <div className="pr-4 space-y-1">
        <div className="flex justify-between">
          <h3 className="font-medium">{bookmark.name}</h3>
          {(bookmark.username === username || bookmark.acls.length > 0) && (
            <div className="flex space-x-2">
              {bookmark.username === username && (
                <Tooltip>
                  <TooltipTrigger>
                    <PiUser></PiUser>
                  </TooltipTrigger>
                  <TooltipContent>Owned by you</TooltipContent>
                </Tooltip>
              )}
              {bookmark.acls.length > 0 && (
                <Tooltip>
                  <TooltipTrigger>
                    <PiUsersThree></PiUsersThree>
                  </TooltipTrigger>
                  <TooltipContent>{groupNames}</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Centered on ({bookmark.lat.toFixed(2)}, {bookmark.lon.toFixed(2)})
          with zoom level {bookmark.zoomLevel}
        </p>
      </div>

      <div className="self-end">
        <BookmarksAddEdit
          mode="edit"
          existingBookmark={bookmark}
        ></BookmarksAddEdit>
      </div>
    </div>
  );
};

interface BookmarksListProps {
  bookmarks: Bookmark[];
}

export const BookmarksList = ({ bookmarks }: BookmarksListProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const { settings, mapDetails } = useSettings();
  const { url: osmUrl, headers: osmHeaders } = useMemo(() => {
    return getOsmUrlHeadersFromSettings(settings);
  }, [settings]);
  const bookmarkLayerConfigs: BaseLayerConfig[] = useMemo(
    () =>
      bookmarks.map((bookmark) => ({
        id: bookmark.uuid,
        type: 'osm',
        title: bookmark.name,
        description: '',
        url: osmUrl,
        headers: osmHeaders,
        center: fromLonLat([bookmark.lon, bookmark.lat]) as [number, number],
        zoom: bookmark.zoomLevel,
      })),
    [bookmarks, osmHeaders, osmUrl]
  );

  const snapshots = useBaseMapSnapshots(bookmarkLayerConfigs);

  const aspectRatio = useMemo(() => {
    if (mapDetails?.width && mapDetails?.height) {
      return mapDetails.width / mapDetails.height;
    }
    return 120 / 80; // Default aspect ratio
  }, [mapDetails?.width, mapDetails?.height]);

  const mapHeight = useMemo(() => {
    if (aspectRatio && mapDetails?.width) {
      return Math.round(mapDetails.width / aspectRatio);
    }
    return 80;
  }, [aspectRatio, mapDetails?.width]);

  const aspectRatioString = useMemo(() => {
    if (mapDetails?.width && mapDetails?.height) {
      return `${mapDetails.width}/${mapDetails.height}`;
    }
    return '120/80'; // Default aspect ratio string
  }, [mapDetails?.width, mapDetails?.height]);

  return (
    <div className="mt-4 grid gap-4">
      <div ref={mapRef} style={{ display: 'none' }}></div>
      {bookmarks.map((bookmark: Bookmark) => (
        <BookmarkItem
          key={bookmark.uuid}
          bookmark={bookmark}
          mapHeight={mapHeight}
          aspectRatioString={aspectRatioString}
          snapshots={snapshots}
        />
      ))}
    </div>
  );
};
